import React, { useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { shouldSuppressRateLimit } from '../utils/httpErrors';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const validatePasswordRules = (password) => {
    const v = password || '';
    if (v.length < 8) return 'La password deve essere almeno 8 caratteri';
    if (!/[A-Z]/.test(v)) return 'La password deve contenere almeno una lettera maiuscola';
    if (!/[a-z]/.test(v)) return 'La password deve contenere almeno una lettera minuscola';
    if (!/[0-9]/.test(v)) return 'La password deve contenere almeno un numero';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(v)) return 'La password deve contenere almeno un carattere speciale (!@#$%^&*(),.?":{}|<>)';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Link non valido o scaduto');
      return;
    }
    const pwdError = validatePasswordRules(form.newPassword);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    setIsLoading(true);
    try {
      const res = await authService.resetPassword(token, form.newPassword, form.confirmPassword);
      toast.success(res?.detail || 'Password aggiornata. Ora puoi accedere.');
      navigate('/login');
    } catch (err) {
      if (shouldSuppressRateLimit(err)) {
        return;
      }
      const msg = err.response?.data?.detail || 'Link non valido o scaduto';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL || ''}/sfondo-login.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-white/40"></div>

      <div className="w-full relative z-10">
        <div className="w-full text-center mb-10 px-4">
          <div className="inline-flex items-center justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo.svg`}
              alt="Motore Gare DS - DS Consulting"
              className="h-20 md:h-28 w-auto mx-auto"
            />
          </div>
          <h1 className="font-display text-3xl font-medium mb-3" style={{ color: '#0F1B2D' }}>
            Imposta nuova password
          </h1>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 relative z-10">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nuova password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.newPassword}
                    onChange={(e) => setForm((p) => ({ ...p, newPassword: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Inserisci la nuova password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conferma password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ripeti la nuova password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-lg text-white font-medium transition-all disabled:opacity-60"
                style={{ backgroundColor: '#0F1B2D' }}
              >
                {isLoading ? 'Salvataggio...' : 'Imposta nuova password'}
              </button>

              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800 underline">
                  Richiedi un nuovo link
                </Link>
                <div className="mt-2">
                  <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 underline">
                    Torna al login
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;


